import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, ResolveFn, Router, RouterStateSnapshot } from '@angular/router';
import { AssetFacade } from '@simOn/asset';
import { assetEventListener } from '@simOn/asset/element/services';
import { ProviderErrorType } from '@simOn/common/http';
import { NotificationService } from '@simOn/common/notification';
import { ProviderConnectionStatusEnum } from '@simOn/common/providers';
import { TPositionWithModel } from '@simOn/common/scan';
import { DevicesFacade, ProviderLocal, WidgetFacade } from '@simOn/device';
import { EventMarkersService } from '@simOn/event/element/data-access';
import { calendarEventListener } from '@simOn/event/element/services';
import { GetLastPosition } from '@simOn/matterport/viewer/models';
import { PrivilegesFacade } from '@simOn/privileges';
import { privilegesEventListener } from '@simOn/privileges/element/services';
import { BaseIoTProviderConnectionDTO } from '@simOn/provider/connection/models';
import { ProviderStore } from '@simOn/provider/state';
import { PublicLinkService } from '@simOn/public-link';
import { RoomFacade } from '@simOn/room';
import { SceneFacade } from '@simOn/scene';
import { SpaceFacade } from '@simOn/space';
import { apartmentEventListener } from '@simOn/space/element/services';
import { TicketFacade } from '@simOn/ticket';
import { ticketEventListener } from '@simOn/ticket/element/services';
import { UpdateUserState } from '@simOn/user/last-visited/models';
import { UsersFacade } from '@simOn/user/last-visited/state';
import { SignalRService } from '@simOn/utils/signalR';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { combineLatest, map, of, switchMap, take, tap } from 'rxjs';
import { DeviceErrorListener } from '../app.routes';

export const apartmentResolverFn: ResolveFn<boolean> = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const apartmentId = route.params.id ?? route.params.apartmentId;
  const spaceFacade = inject(SpaceFacade);
  const providerStore = inject(ProviderStore);

  const assetFacade = inject(AssetFacade);
  const roomFacade = inject(RoomFacade);
  const privilegesFacade = inject(PrivilegesFacade);
  const devicesFacade = inject(DevicesFacade);
  const widgetFacade = inject(WidgetFacade);
  const sceneFacade = inject(SceneFacade);
  const deviceErrorListener = inject(DeviceErrorListener);
  const ticketFacade = inject(TicketFacade);
  const signalR = inject(SignalRService);
  const publicLinkService = inject(PublicLinkService);
  const router = inject(Router);
  const usersFacade = inject(UsersFacade);
  const notificationService = inject(NotificationService);
  const eventMarkersService = inject(EventMarkersService);
  deviceErrorListener.subscribe();
  const loggedUser = inject(OidcSecurityService).getUserData();
  spaceFacade.getApartment();
  !publicLinkService.isPublicLinkModeActive() &&
    apartmentEventListener(signalR, spaceFacade.selectedApartmentId$, loggedUser, {
      CurrentApartmentUsers: (apartmentUsers) => {
        usersFacade.setSelectedApartmentUsers(apartmentUsers);
      },
      UserEnteredApartment: ({ userId }) => {
        const userState: UpdateUserState = {
          id: userId,
          isActive: true,
          lastActivity: new Date().toJSON()
        };
        usersFacade.updateApartmentUserState(userState);
      },
      UserLeftApartment: ({ userId }) => {
        const userState: UpdateUserState = {
          id: userId,
          isActive: false,
          lastActivity: new Date().toJSON()
        };
        usersFacade.updateApartmentUserState(userState);
      },
      UserGrantAccessToApartment: (apartmentUser) => {
        usersFacade.addApartmentUserState(apartmentUser);
      },
      UserLostAccessToApartment: (apartmentUser) => {
        usersFacade.deleteApartmentUserState(apartmentUser);
      }
    });
  localStorage.setItem('objectDefaultVisibility', 'false');
  return combineLatest([spaceFacade.selectedApartmentId$, spaceFacade.apartmentLoaded$]).pipe(
    tap(([selectedApartmentId]) => {
      if (selectedApartmentId) {
        const position: TPositionWithModel = GetLastPosition(apartmentId);
        if (position && position?.modelId) spaceFacade.setSelectedSpaceModelId(position.modelId);
        !publicLinkService.isPublicLinkModeActive()
          ? privilegesEventListener(signalR, router, {
              UsersLostAccessToApartment: ({ apartmentId, userIds }) => {
                userIds.forEach((userId) => usersFacade.deleteApartmentUserState({ apartmentId, userId }));
              },
              LostAccessToApartment: ({ apartmentId }) => {
                spaceFacade.removeApartment(apartmentId);
                spaceFacade.leaveApartment();
                notificationService.openWarnSnackBar(
                  $localize`:@@APARTMENT_ACCESS_LOST_INFO:You have lost access to this space`
                );
              },
              UserApartmentAccessModified: () => {
                privilegesFacade.loadPrivileges();
              }
            })
          : publicLinkService.allows('canViewTickets') && ticketFacade.getTickets(selectedApartmentId, true);
        !publicLinkService.isPublicLinkModeActive() &&
          assetEventListener(signalR, {
            AssetCreated: ({ assetId }) => {
              assetFacade.getAsset(assetId);
            },
            AssetsCreated: ({ assetIds }) => {
              assetIds.forEach((assetId) => assetFacade.getAsset(assetId));
            },
            AssetModified: ({ assetId }) => {
              assetFacade.getAsset(assetId);
            },
            AssetsModified: ({ assetIds }) => {
              // Only the assets already in the store are the ones this user can see,
              // so refresh those instead of requesting every id from the batch.
              const loadedIds = new Set(assetFacade.selectAllAssets().map((asset) => asset.id));
              assetIds.filter((assetId) => loadedIds.has(assetId)).forEach((assetId) => assetFacade.getAsset(assetId));
            },
            AssetsDeleted: ({ assetIds }) => {
              assetFacade.removeAssetsFromStore(assetIds);
            }
          });
        !publicLinkService.isPublicLinkModeActive() &&
          calendarEventListener(signalR, {
            EventCreated: () => {
              eventMarkersService.refresh();
            },
            EventModified: () => {
              eventMarkersService.refresh();
            },
            EventDeleted: () => {
              console.log('deleted');

              eventMarkersService.refresh();
            }
          });
        !publicLinkService.isPublicLinkModeActive() &&
          ticketEventListener(signalR, ticketFacade, selectedApartmentId, {
            TicketCreated: ({ ticketId }) => {
              ticketFacade.getTicket(ticketId);
            },
            TicketModified: ({ ticketId }) => {
              ticketFacade.handleTicketModified(ticketId);
            },
            TicketDeleted: ({ ticketId }) => {
              ticketFacade.deleteTicketFromStore(ticketId);
            }
          });
        roomFacade.getRooms();
        // A public link without these capabilities has no API rights for them - the backend answers 500.
        if (publicLinkService.allows('canViewDevices')) {
          widgetFacade.loadWidgets();
          devicesFacade.loadDevicesStructure();
        }
        if (publicLinkService.allows('canStartScenes')) sceneFacade.getSimlabScenes();
        assetFacade.getAssets();
      }
    }),
    switchMap((selectedApartmentId) =>
      of(providerStore.getIotProviders()).pipe(
        tap((providerConnections: BaseIoTProviderConnectionDTO[]) => {
          if (selectedApartmentId && publicLinkService.allows('canViewDevices'))
            devicesFacade.loadDevicesProperties(
              providerConnections.map(
                ({ iotProvider, masterDeviceId }) =>
                  ({
                    iotProvider,
                    masterDeviceId,
                    providerConnectionStatus: ProviderConnectionStatusEnum.Unknown
                  }) as ProviderLocal
              )
            );
          devicesFacade.LoadDevicePropertiesFailure$.pipe(
            tap(({ error, masterDeviceId }) => {
              if (
                [
                  ProviderErrorType.SmartApiAuthorizationProviderNoAuthorization,
                  ProviderErrorType.SmartApiAuthorization,
                  ProviderErrorType.SmartApiAuthorizationProviderLoggedOut
                ].includes(error.error.ErrorCode)
              ) {
                spaceFacade.updateApartmentProviderStatus(
                  'apartmentId',
                  masterDeviceId,
                  ProviderConnectionStatusEnum.LoggedOut
                );
              }
            })
          );
        })
      )
    ),
    switchMap(() => {
      return combineLatest([spaceFacade.defaultSpaceModelId$, spaceFacade.selectedSpaceModelId$]).pipe(
        take(1),
        tap(([defaultSpaceModelId, selectedSpaceModelId]) => {
          spaceFacade.setSelectedSpaceModelId(selectedSpaceModelId || defaultSpaceModelId);
        })
      );
    }),
    take(1),
    map(() => true)
  );
};
